package com.example.positionahead.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.positionahead.domain.AlertEvaluator
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PortfolioProjectionPoint
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.PriceAlert
import com.example.positionahead.model.RebalanceTrade
import com.example.positionahead.model.objectiveLabel
import com.example.positionahead.ui.components.AccountSwitcher
import com.example.positionahead.ui.components.CollapsibleSection
import com.example.positionahead.ui.components.PositionRow
import com.example.positionahead.ui.components.RecommendationCard
import com.example.positionahead.ui.components.SectionHeader
import com.example.positionahead.ui.theme.gainColor
import com.example.positionahead.ui.theme.lossColor
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatGainLoss
import com.example.positionahead.util.formatPercent
import com.example.positionahead.viewmodel.PortfolioUiState

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DashboardScreen(
    uiState: PortfolioUiState,
    planSellHintSymbol: String? = null,
    onDismissPlanSellHint: () -> Unit = {},
    onNavigateToPlan: () -> Unit = {},
    onRefresh: () -> Unit,
    onSelectAccount: (String) -> Unit,
    onCreateAccount: (name: String, copyFromId: String?, colorHex: String, startDate: String?, notes: String) -> Unit,
    onUpdateAccount: (id: String, name: String, colorHex: String, startDate: String?, notes: String) -> Unit,
    onDeleteAccount: (String) -> Unit,
    onRemovePosition: (String) -> Unit,
    onAddLot: (positionId: String, shares: Double, costBasis: Double, purchaseDate: String?, note: String?) -> Unit,
    onUpdateLot: (positionId: String, lotId: String, shares: Double, costBasis: Double, purchaseDate: String?, note: String?) -> Unit,
    onSellLot: (positionId: String, lotId: String, sharesToSell: Double) -> Unit,
    onDismissAlerts: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var recommendationsExpanded by remember { mutableStateOf(false) }
    var expandedPositionId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(planSellHintSymbol, uiState.summary.positions) {
        planSellHintSymbol?.let { symbol ->
            val match = uiState.summary.positions.find {
                it.position.symbol.equals(symbol, ignoreCase = true)
            }
            expandedPositionId = match?.position?.id
        }
    }

    Column(modifier = modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        TopAppBar(
            title = {
                AccountSwitcher(
                    accounts = uiState.portfolios,
                    activeAccountId = uiState.activePortfolioId,
                    onSelectAccount = onSelectAccount,
                    onCreateAccount = onCreateAccount,
                    onUpdateAccount = onUpdateAccount,
                    onDeleteAccount = onDeleteAccount,
                )
            },
            actions = {
                if (uiState.isRefreshing) {
                    CircularProgressIndicator(
                        modifier = Modifier.padding(end = 16.dp).height(24.dp),
                        strokeWidth = 2.dp,
                        color = MaterialTheme.colorScheme.primary
                    )
                } else {
                    TextButton(onClick = onRefresh) {
                        Text(
                            "Refresh",
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.primary
                        )
                    }
                }
            },
            colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent)
        )

        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(bottom = 32.dp),
        ) {
            item {
                PortfolioHeroSection(uiState.summary)
            }

            planSellHintSymbol?.let { symbol ->
                item {
                    PaddingWrapper {
                        Card(
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.45f),
                            ),
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            Row(
                                modifier = Modifier.padding(12.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text(
                                    "From Plan: sell about the suggested dollar amount of $symbol (expand the row below → Sell lot).",
                                    modifier = Modifier.weight(1f),
                                    style = MaterialTheme.typography.bodySmall,
                                )
                                TextButton(onClick = onDismissPlanSellHint) {
                                    Text("Dismiss")
                                }
                            }
                        }
                    }
                }
            }

            if (uiState.usesLiveQuotes && uiState.totalHoldingsCount > 0 &&
                uiState.livePricedHoldingsCount < uiState.totalHoldingsCount
            ) {
                item {
                    PaddingWrapper {
                        DataSyncBanner(
                            liveCount = uiState.livePricedHoldingsCount,
                            totalCount = uiState.totalHoldingsCount,
                            lastUpdated = uiState.lastUpdated,
                        )
                    }
                }
            }

            item {
                PaddingWrapper {
                    PlanTabTeaser(onClick = onNavigateToPlan)
                }
            }

            if (uiState.triggeredAlerts.isNotEmpty()) {
                item {
                    PaddingWrapper {
                        TriggeredAlertsBanner(
                            alerts = uiState.triggeredAlerts,
                            onDismiss = onDismissAlerts,
                        )
                    }
                }
            }

            item {
                PaddingWrapper {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(24.dp))
                            .background(MaterialTheme.colorScheme.surface)
                            .padding(16.dp)
                    ) {
                        CollapsibleSection(
                            title = "Insights & Strategy",
                            expanded = recommendationsExpanded,
                            onToggle = { recommendationsExpanded = !recommendationsExpanded },
                        ) {
                            GoalSummaryChip(uiState.goal)
                            Text(
                                text = "Personalized analysis for your ${uiState.goal.objectiveLabel()} strategy.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.padding(bottom = 8.dp),
                            )
                            if (uiState.recommendations.isEmpty()) {
                                Text(
                                    "Add positions to unlock premium insights.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    fontStyle = androidx.compose.ui.text.font.FontStyle.Italic
                                )
                            } else {
                                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    uiState.recommendations.forEach { recommendation ->
                                        RecommendationCard(recommendation = recommendation)
                                    }
                                }
                            }
                        }
                    }
                }
            }

            item {
                PaddingWrapper {
                    SectionHeader(
                        title = "Holdings",
                        modifier = Modifier.padding(top = 24.dp, bottom = 8.dp),
                    )
                }
            }

            if (uiState.summary.positions.isEmpty()) {
                item {
                    PaddingWrapper {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 48.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                "Start building your wealth.",
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            } else {
                items(uiState.summary.positions, key = { it.position.id }) { snapshot ->
                    val analysis = uiState.positionAnalyses[snapshot.position.symbol.uppercase()]
                    PositionRow(
                        snapshot = snapshot,
                        analysis = analysis,
                        expanded = expandedPositionId == snapshot.position.id,
                        onToggleExpand = {
                            expandedPositionId = if (expandedPositionId == snapshot.position.id) {
                                null
                            } else {
                                snapshot.position.id
                            }
                        },
                        onRemove = { onRemovePosition(snapshot.position.id) },
                        onAddLot = { shares, costBasis, date, note ->
                            onAddLot(snapshot.position.id, shares, costBasis, date, note)
                        },
                        onUpdateLot = { lotId, shares, costBasis, date, note ->
                            onUpdateLot(snapshot.position.id, lotId, shares, costBasis, date, note)
                        },
                        onSellLot = { lotId, sharesToSell ->
                            onSellLot(snapshot.position.id, lotId, sharesToSell)
                        },
                    )
                    HorizontalDivider(
                        modifier = Modifier.padding(horizontal = 24.dp),
                        color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                    )
                }
            }

            item {
                PaddingWrapper {
                    Text(
                        text = "Last synced: ${uiState.lastUpdated}",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(top = 32.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun PaddingWrapper(content: @Composable () -> Unit) {
    Column(modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp)) {
        content()
    }
}

@Composable
private fun PortfolioHeroSection(summary: PortfolioSummary) {
    val totalGain = summary.totalUnrealizedGainLoss
    val totalGainPercent = summary.totalUnrealizedGainLossPercent
    val isGain = totalGain >= 0
    val accentColor = if (isGain) gainColor else lossColor

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(20.dp)
            .clip(RoundedCornerShape(32.dp))
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        accentColor.copy(alpha = 0.15f),
                        Color.Transparent
                    )
                )
            )
            .padding(24.dp)
    ) {
        Column {
            Text(
                text = "Total Balance",
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = formatCurrency(summary.totalMarketValue),
                style = MaterialTheme.typography.displayMedium.copy(
                    fontSize = 42.sp,
                    fontWeight = FontWeight.Black,
                    letterSpacing = (-1.5).sp
                ),
            )
            Spacer(modifier = Modifier.height(12.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .clip(RoundedCornerShape(12.dp))
                    .background(accentColor.copy(alpha = 0.1f))
                    .padding(horizontal = 12.dp, vertical = 6.dp)
            ) {
                Text(
                    text = "${if (isGain) "+" else ""}${formatCurrency(totalGain)} (${formatPercent(totalGainPercent)})",
                    style = MaterialTheme.typography.bodyMedium,
                    color = accentColor,
                    fontWeight = FontWeight.ExtraBold,
                )
                Text(
                    text = " • All Time",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.Medium,
                )
            }
        }
    }
}

@Composable
private fun GoalSummaryChip(goal: FinancialGoal) {
    Row(
        modifier = Modifier
            .padding(bottom = 8.dp)
            .clip(RoundedCornerShape(8.dp))
            .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.1f))
            .padding(horizontal = 8.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = goal.objectiveLabel(),
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        )
    }
}

@Composable
private fun DataSyncBanner(
    liveCount: Int,
    totalCount: Int,
    lastUpdated: String,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.tertiaryContainer.copy(alpha = 0.35f),
        ),
        shape = RoundedCornerShape(16.dp),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                "Live prices: $liveCount of $totalCount holdings",
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Bold,
            )
            Text(
                "$lastUpdated. Values may not match Robinhood until every holding has a live quote. " +
                    "Use average cost in CSV import; dividends/splits are not synced yet.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun PlanTabTeaser(onClick: () -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.35f)),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text("Compare strategies in Plan", fontWeight = FontWeight.Black, style = MaterialTheme.typography.titleSmall)
            Text(
                "Side-by-side current vs Alt B/C, normalized dividends, trades, and save as theoretical portfolio.",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                "Open Plan →",
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.primary,
            )
        }
    }
}

@Composable
private fun ProjectionComparisonCard(
    projections: List<PortfolioProjectionPoint>,
    trades: List<RebalanceTrade>,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        shape = RoundedCornerShape(20.dp),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                "3 / 6 / 12-month outlook",
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.Black,
            )
            Text(
                if (trades.isNotEmpty()) {
                    "Illustrative comparison if you follow the suggested dollar trades vs staying put."
                } else {
                    "Illustrative forward projection using recent return trends (not a guarantee)."
                },
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            projections.forEach { point ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                ) {
                    Text("${point.months} mo", fontWeight = FontWeight.SemiBold)
                    Column(horizontalAlignment = Alignment.End) {
                        Text(
                            "Now path ${formatCurrency(point.currentPortfolioValue)}",
                            style = MaterialTheme.typography.labelMedium,
                        )
                        Text(
                            "Suggested ${formatCurrency(point.suggestedPortfolioValue)} " +
                                "(${if (point.deltaValue >= 0) "+" else ""}${formatCurrency(point.deltaValue)})",
                            style = MaterialTheme.typography.labelMedium,
                            color = if (point.deltaValue >= 0) gainColor else lossColor,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TriggeredAlertsBanner(
    alerts: List<PriceAlert>,
    onDismiss: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.3f)),
        shape = RoundedCornerShape(20.dp),
        elevation = CardDefaults.cardElevation(0.dp)
    ) {
        Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(
                "Market Alerts", 
                style = MaterialTheme.typography.titleSmall, 
                fontWeight = FontWeight.Black,
                color = MaterialTheme.colorScheme.error
            )
            alerts.take(2).forEach { alert ->
                Text(
                    AlertEvaluator.describe(alert), 
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Medium
                )
            }
            TextButton(
                onClick = onDismiss,
                modifier = Modifier.align(Alignment.End)
            ) { 
                Text("Dismiss", color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Bold) 
            }
        }
    }
}
